/**
 * sportsDataService: the single boundary between the product and any
 * source of football data (PRD §31, Implementation Guide §20).
 *
 * Components call these methods and never learn which provider answered.
 *
 * Two sources run side by side, on purpose:
 *
 *   - **Real NFL data** (`espnProvider`): the actual schedule, scores and
 *     play-by-play. This is what a beta tester wants to see.
 *   - **The practice game** (`mockProvider`): a scripted, simulated game
 *     that can be started at any moment and finishes in thirteen minutes.
 *     Without it there is nothing to demo between real kickoffs, and a
 *     feedback session would take three hours.
 *
 * Every record carries `isSimulated`, and the UI badges the two
 * differently, so a simulated game is never mistaken for a real one
 * (Implementation Guide §21).
 *
 * Which game goes to which provider is decided by the game id prefix, so
 * adding or removing a source doesn't touch a single component.
 */

import mockProvider from "./mockProvider";
import espnProvider, { scheduleRefreshMs, withFetchedAt } from "./espnProvider";
import { loadBackend } from "../backend";
import { weekHeading } from "../seasons/seasonService";
import { GAME_STATUS, isOffSchedule } from "../../utils/gameEngine";

/**
 * Cache windows and poll intervals are one decision, made by the provider
 * that owns the cache and re-exported here so hooks never reach past this
 * boundary to get them.
 */
export { LIVE_POLL_MS, scheduleRefreshMs } from "./espnProvider";

const PROVIDERS = {
  mock: mockProvider,
  nfl: espnProvider,
};

const requestedProviderId = String(
  process.env.REACT_APP_SPORTS_PROVIDER ?? "nfl"
).trim();

/** Set REACT_APP_INCLUDE_PRACTICE_GAME=false to hide the simulated game. */
const includePracticeGame =
  String(process.env.REACT_APP_INCLUDE_PRACTICE_GAME ?? "true").trim() !== "false";

let liveProvider = null;

/** The provider that answers for real games. */
export function getProvider() {
  if (liveProvider) return liveProvider;

  liveProvider = PROVIDERS[requestedProviderId];

  if (!liveProvider) {
    // eslint-disable-next-line no-console
    console.error(
      `[I Don't Know Football] Unknown sports provider "${requestedProviderId}". ` +
        "Falling back to the practice game only."
    );
    liveProvider = mockProvider;
  }

  return liveProvider;
}

/** Route one game to whichever provider owns it. */
function providerForGame(gameId) {
  return String(gameId ?? "").startsWith("mock-") ? mockProvider : getProvider();
}

export function getProviderInfo() {
  const provider = getProvider();
  return {
    id: provider.id,
    label: provider.label,
    isSimulated: Boolean(provider.isSimulated),
    attribution: provider.attribution ?? null,
    requestedId: requestedProviderId,
    includesPracticeGame: includePracticeGame,
  };
}

/* ------------------------------------------------- the stored schedule */

/**
 * THE STORED COPY, AND WHY IT LIVES AT THIS BOUNDARY
 * --------------------------------------------------
 * Fetching the schedule from the provider on every visit is slow for the
 * reader and, once the app has more than a handful of them, expensive.
 * So the last successful answer is stored (see
 * `services/backend/backendContract.js#readCache`), served immediately on
 * the next visit, and revalidated in the background.
 *
 * It lives here rather than inside `espnProvider` because it is
 * storage-shaped, not provider-shaped: nothing about it knows what ESPN
 * returns. A second provider added tomorrow inherits the whole behaviour
 * by being routed through this function, and the entry it reads is
 * refused unless it was written by that same provider.
 *
 * THREE TIERS, EACH ANSWERING A DIFFERENT QUESTION
 * ------------------------------------------------
 *   1. `memory` here: has this session already got this list? Answers the
 *      60 second poll for free. Without it every poll would be a stored
 *      read per user per minute, which is the cost this whole feature
 *      exists to avoid.
 *   2. The backend cache: did a previous session, on any device, leave a
 *      copy? Read at most ONCE per key per session, on the first call,
 *      because after that tier 1 is always at least as fresh.
 *   3. The provider itself, which runs on every call regardless.
 */

const CACHE_NAMESPACE = "schedule";

/**
 * The rate an all-idle schedule is polled at, taken from the provider's
 * own policy rather than restated here. Anything the policy wants read
 * back sooner than this has a game running in it or a kickoff due.
 */
const IDLE_REFRESH_MS = scheduleRefreshMs([]);

/**
 * How old a stored schedule of games days away may be and still be shown
 * while the live copy is on its way.
 *
 * Deliberately much longer than the poll interval, because these are two
 * different questions. The poll asks "could this have changed?", and the
 * answer for a live game is "yes, seconds ago". A stored entry is asked
 * "is this worth painting for the half second before the real answer
 * lands?", and for a fixture list days out the answer stays yes for
 * hours. Six of them: within half a day the fixtures, kickoff times and
 * final scores on that list are the same ones, and past it a whole slate
 * has usually been played, so the list would be wrong about which games
 * are still to come.
 *
 * This window NEVER applies to a payload with a game running in it. See
 * `cacheTtlMs`.
 */
const CACHE_IDLE_TTL_MS = 6 * 60 * 60_000;

/**
 * The soonest a successful fetch will pay to store itself again.
 *
 * A cost decision rather than a freshness one, which is why it is not the
 * TTL. Storing the identical list every five minutes for the whole of a
 * long session buys nothing and is billed every time, so a write happens
 * only when the payload has actually changed and this long has passed.
 */
const CACHE_WRITE_MIN_INTERVAL_MS = 5 * 60_000;

/** key -> {games, fetchedAt, provider}: tier 1, this session's copy. */
const memory = new Map();

/** key -> Promise: tier 2, created once per key per session and kept. */
const storedReads = new Map();

/** key -> {at, signature}: what this session has already paid to store. */
const writes = new Map();

/**
 * A season that has finished never changes again, so its stored week could
 * honestly be trusted forever. The storage layer disagrees and caps
 * everything at `CACHE_HARD_MAX_AGE_MS`, which is a fortnight, so this
 * number is really "as long as you are allowed to". Two days keeps an
 * archive instant across a week of visits while still re-confirming it
 * against ESPN often enough that a correction to an old boxscore reaches
 * the screen.
 */
const CACHE_ARCHIVE_TTL_MS = 2 * 24 * 60 * 60_000;

/**
 * One document per distinct list, and the limit is part of what makes it
 * distinct: the provider truncates to it, so a list stored for the home
 * page's six games cannot answer the Games page's sixteen.
 *
 * TWO KEY SHAPES, BECAUSE THERE ARE TWO QUESTIONS
 * -----------------------------------------------
 * `upcoming-16` is the rolling window: whatever is on around now, spanning
 * three weeks and sometimes two phases of a season. It carries no season
 * in its name and must not start carrying one. Deriving it would mean
 * asking ESPN which season it is BEFORE the schedule could be read from
 * store, which would put the season endpoint on the critical path of the
 * one screen that has to work. It is already season-scoped in the only way
 * that matters: it can only ever be written by the live path, which can
 * only ever ask for now.
 *
 * `season-2015-t2-w1-l16` is a named week of a named season. Every part of
 * the identity is in the name: year, phase, week and limit. This is the
 * key that stops two seasons poisoning each other, and it is why browsing
 * 2015 cannot leave anything behind that the live schedule would read.
 */
const scheduleCacheKey = ({ limit } = {}) =>
  `upcoming-${Number.isFinite(limit) ? limit : "default"}`;

export const seasonScheduleCacheKey = ({ year, seasonType, week, limit } = {}) =>
  `season-${year}-t${seasonType}-w${week}-l${
    Number.isFinite(limit) ? limit : "default"
  }`;

const containsLiveGame = (games) =>
  games.some((game) => game?.status === GAME_STATUS.IN_PROGRESS);

/**
 * Games whose story is not over, even though they are not in progress.
 *
 * A suspended or delayed game carries a real partial score and will change
 * when play resumes. A postponed one moves to a different day. None of them
 * are IN_PROGRESS, so `containsLiveGame` waves them through, and the idle
 * policy then hands the payload a six hour window: long enough to still be
 * serving "stopped at 14-7" after the game has finished 31-17.
 *
 * Kept separate from `containsLiveGame` on purpose. That predicate answers
 * "how fast does this need re-reading", which the read paths also ask. This
 * one answers "is this safe to keep at all", which only the writer asks.
 */
const containsUnsettledGame = (games) =>
  games.some((game) => isOffSchedule(game?.status));

/**
 * How long a stored payload may be trusted, decided by what is on it.
 *
 * THIS IS THE SAFETY-CRITICAL LINE IN THIS FILE. A fixture list for next
 * Sunday is the same list an hour from now. A list with a game in
 * progress on it carries a score, and a score is wrong the moment
 * somebody scores. So the moment the schedule policy wants a list read
 * back faster than an idle one, which is when a game is running or a
 * kickoff is due, its stored copy gets exactly that short window and no
 * more. Past it we would rather show a loading state than a score we
 * cannot stand behind.
 */
function cacheTtlMs(games, now) {
  const refresh = scheduleRefreshMs(games, { now });
  return refresh < IDLE_REFRESH_MS ? refresh : CACHE_IDLE_TTL_MS;
}

/**
 * The same decision for an archived week, with a longer idle window.
 *
 * The live check is kept rather than dropped. A season that has finished
 * cannot contain a game in progress, so this branch should never fire, and
 * that is exactly why it stays: if it ever does fire, something has gone
 * wrong upstream and the right response is still to refuse to serve a
 * score nobody can refresh.
 */
function archiveCacheTtlMs(games, now) {
  const refresh = scheduleRefreshMs(games, { now });
  return refresh < IDLE_REFRESH_MS ? refresh : CACHE_ARCHIVE_TTL_MS;
}

/**
 * Accept a stored entry, or report a miss.
 *
 * Attribution has to survive storage intact, because the one thing this
 * app must never do is present the practice game as real football. An
 * entry is refused unless the provider that wrote it is the provider
 * asking for it, and refused outright if a simulated game is inside it:
 * only real fixtures are ever written here, so a simulated one means the
 * entry came from somewhere this build should not trust.
 */
function acceptStored(hit, providerId) {
  if (!hit || !Array.isArray(hit.value) || hit.value.length === 0) return null;
  if (hit.provider !== providerId) return null;
  if (hit.value.some((game) => !game || game.isSimulated)) return null;

  return { games: hit.value, fetchedAt: hit.fetchedAt, provider: hit.provider };
}

/**
 * Tier 2, read once per key per session.
 *
 * The promise is kept rather than the result, so two components mounting
 * at the same moment share one read instead of racing to make two, and a
 * miss is remembered as firmly as a hit.
 */
function readStoredSchedule(key) {
  if (!storedReads.has(key)) {
    storedReads.set(
      key,
      loadBackend()
        .then((backend) => backend?.readCache?.(CACHE_NAMESPACE, key) ?? null)
        // A cache that cannot be read is a cache miss, never an error the
        // reader sees: there is a live fetch already in flight behind it.
        .catch(() => null)
    );
  }

  return storedReads.get(key);
}

/** Tier 1 then tier 2. Never throws, and never reads storage twice. */
async function readSchedule(key, providerId, { backendCache = true } = {}) {
  const thisSession = memory.get(key);
  if (thisSession) return thisSession;
  if (!backendCache) return null;

  const entry = acceptStored(await readStoredSchedule(key), providerId);
  if (entry) memory.set(key, entry);
  return entry;
}

/**
 * Write the live answer back, if it is worth paying for.
 *
 * A live game's list is never written at all. It could only be served to
 * somebody within thirty seconds of the write, which in practice is
 * nobody, and storing a score in a collection any signed-in client can
 * write is exactly the extension `docs/security/cache-trust-model.md`
 * says to come back and reconsider. So the stored copy stays what it is
 * good at: fixtures, kickoff times and finished games.
 *
 * Failing to write is never a reason to fail the schedule, so everything
 * here is swallowed.
 */
async function saveSchedule(key, games, providerId, now, { backendCache = true } = {}) {
  if (!backendCache) return;

  try {
    const fetchedAt = games.fetchedAt;
    // No honest timestamp, no write: a stored entry is aged against this
    // number and we will not invent one.
    if (typeof fetchedAt !== "number" || !Number.isFinite(fetchedAt)) return;
    if (games.length === 0 || containsLiveGame(games)) return;
    if (containsUnsettledGame(games)) return;

    const signature = JSON.stringify(games);
    const previous = writes.get(key);
    if (
      previous &&
      (previous.signature === signature ||
        now - previous.at < CACHE_WRITE_MIN_INTERVAL_MS)
    ) {
      return;
    }

    // Recorded before the await so two overlapping polls cannot both pay.
    writes.set(key, { at: now, signature });

    const backend = await loadBackend();
    if (!backend?.writeCache) return;

    await backend.writeCache(CACHE_NAMESPACE, key, games, {
      provider: providerId,
      fetchedAt,
    });
  } catch {
    // Keep the interval so a failing write cannot be retried every poll,
    // but drop the signature so the next attempt is allowed to differ.
    writes.set(key, { at: now, signature: null });
  }
}

const hidden = (value) => ({
  value,
  enumerable: false,
  configurable: true,
  writable: true,
});

/**
 * One answer from this service: the games, plus everything the caller
 * needs to describe them honestly.
 *
 * The metadata rides alongside the list rather than inside it, because
 * every element of the list is a game. Non-enumerable so that nothing
 * which walks or serialises the list trips over it, including the write
 * back into storage.
 *
 * @param {Array} games
 * @param {object} meta
 * @param {number|null} meta.fetchedAt when this data left the network
 * @param {string|null} meta.source    the provider that produced it
 * @param {boolean} meta.fromCache     true when this is the stored copy
 * @param {boolean} meta.stale         true when the live fetch failed and
 *                                     this is the last copy we can vouch
 *                                     for, which is older than it looks
 * @param {Error|null} meta.error      why the live schedule is missing
 * @param {number} meta.revision       1 for the stored copy delivered
 *   immediately, 2 for the live one that follows it. A promise and a
 *   callback can be scheduled in either order, so the number, not the
 *   order of arrival, says which answer is the newer one.
 * @param {number} meta.unmapped       how many games the provider listed
 *   for this week and could not map to a club we know. Only ever non-zero
 *   for an old season, where clubs have moved city since. Zero rather than
 *   null on a stored copy, because the count is a fact about a response
 *   and the stored copy is not one: the live answer that follows carries
 *   the real number a moment later.
 */
function scheduleAnswer(games, meta = {}) {
  const list = withFetchedAt(games, meta.fetchedAt ?? null);

  return Object.defineProperties(list, {
    source: hidden(meta.source ?? null),
    fromCache: hidden(Boolean(meta.fromCache)),
    stale: hidden(Boolean(meta.stale)),
    scheduleUnavailable: hidden(meta.error ?? null),
    revision: hidden(meta.revision ?? 1),
    unmapped: hidden(Number(meta.unmapped) || 0),
  });
}

/* --------------------------------------------------------------- API */

/**
 * The schedule: real NFL games first, with the practice game alongside.
 *
 * STALE WHILE REVALIDATE, IN THREE SENTENCES
 * ------------------------------------------
 * The live fetch always runs. If a stored copy is inside its TTL, this
 * promise resolves with that copy straight away so the list paints
 * immediately, and the live answer follows through `onRevalidated`. If
 * there is no usable stored copy, the promise waits for the live answer,
 * which is the loading state, and is the right outcome when the
 * alternative is a score we cannot stand behind.
 *
 * A promise resolves once, so the second answer needs somewhere to go:
 * pass `onRevalidated` and it is called with exactly the same shape this
 * function returns. It is optional. Ignore it and you get the ordinary
 * one-answer behaviour, cache first if there is one.
 *
 * The practice game is never stored and never read from storage. It is
 * computed from the wall clock every time, for nothing, and a stored copy
 * of it would be a simulated game frozen at somebody else's idea of now.
 *
 * If the real feed is unreachable we still return the practice game
 * rather than an empty screen, and flag that the live schedule failed so
 * the UI can say so honestly.
 *
 * @param {object} [options]
 * @param {number} [options.limit]
 * @param {number} [options.now] epoch ms, for tests
 * @param {(games: Array) => void} [options.onRevalidated] called at most
 *   once, with the live answer, when the stored copy was served first.
 * @param {boolean} [options.backendCache] false keeps the in-session memory
 *   tier but skips storage-backed cache reads and writes. Public browse mode
 *   uses this so a signed-out schedule view does not load Firebase only to
 *   ask for a saved copy.
 */
export async function getUpcomingGames({
  onRevalidated,
  backendCache = true,
  ...options
} = {}) {
  const provider = getProvider();
  const now = options.now ?? Date.now();

  if (provider === mockProvider) {
    // Nothing to cache: every one of these is computed locally from the
    // clock, costs nothing and is stale the moment it is stored.
    const simulated = await mockProvider.getUpcomingGames(options);
    return scheduleAnswer(simulated, {
      fetchedAt: now,
      source: mockProvider.id,
      revision: 2,
    });
  }

  const key = scheduleCacheKey(options);

  // Started before anything is awaited, and resolved to a result object
  // rather than left to reject: it is read from two places below and an
  // unobserved rejection here is a noisy warning about nothing.
  const live = provider.getUpcomingGames(options).then(
    (real) => {
      memory.set(key, {
        games: [...real],
        fetchedAt: real.fetchedAt ?? now,
        provider: provider.id,
        unmapped: Number(real.unmapped) || 0,
      });
      // Deliberately not awaited: a reader waiting on a cache write is a
      // reader waiting on storage to show them something it already has.
      // `saveSchedule` swallows its own failures, so nothing escapes.
      saveSchedule(key, real, provider.id, now, { backendCache });
      return { ok: true, real };
    },
    (error) => ({ ok: false, error })
  );

  const practice = includePracticeGame
    ? await mockProvider.getUpcomingGames({ ...options, limit: 1 })
    : [];

  const stored = await readSchedule(key, provider.id, { backendCache });

  const fromStored = (entry, extra) =>
    scheduleAnswer([...entry.games, ...practice], {
      fetchedAt: entry.fetchedAt,
      source: entry.provider,
      fromCache: true,
      ...extra,
    });

  const fromLive = (real, revision) =>
    scheduleAnswer([...real, ...practice], {
      // Null, not now, if a provider cannot say: an unknown age is better
      // told as unknown.
      fetchedAt: real.fetchedAt ?? null,
      source: provider.id,
      revision,
    });

  if (stored && now - stored.fetchedAt < cacheTtlMs(stored.games, now)) {
    live.then((result) => {
      if (!onRevalidated) return;

      onRevalidated(
        result.ok
          ? fromLive(result.real, 2)
          : // Still the copy we already painted, now with the reason it
            // could not be confirmed against the provider.
            fromStored(stored, { stale: true, error: result.error, revision: 2 })
      );
    });

    return fromStored(stored, { revision: 1 });
  }

  const result = await live;
  if (result.ok) return fromLive(result.real, 2);

  // The live fetch failed. A stored copy is better than an empty screen,
  // but not at any age: one with a game running on it is a score nobody
  // can refresh, and that is the single thing this app must never show.
  if (stored && !containsLiveGame(stored.games)) {
    return fromStored(stored, { stale: true, error: result.error, revision: 2 });
  }

  if (practice.length > 0) {
    return scheduleAnswer(practice, {
      fetchedAt: now,
      source: mockProvider.id,
      error: result.error,
      revision: 2,
    });
  }

  throw result.error;
}

/**
 * One named week of one named season: the archive.
 *
 * Same three tiers, same stale-while-revalidate, same refusal to serve a
 * score it cannot stand behind. Three things are deliberately different
 * from `getUpcomingGames`, and each one is a decision rather than an
 * omission:
 *
 *   1. NO PRACTICE GAME. The practice game answers "there is nothing on
 *      right now, give me something to watch", which is a question about
 *      now. Putting a scripted simulation inside a record of what happened
 *      in week 1 of 2015 would be a false claim about that week, and this
 *      product's one unbreakable rule is that a simulated game is never
 *      mistaken for a real one. It stays exactly where it was, on the
 *      current season's list, which this function does not touch.
 *
 *   2. NO FALLBACK TO THE PRACTICE GAME ON FAILURE either, for the same
 *      reason. If the archive cannot be loaded the caller gets the error
 *      and the page says so.
 *
 *   3. A LONGER STORED WINDOW, because a finished week is finished.
 *
 * @param {object} options
 * @param {number} options.year
 * @param {number} options.seasonType 1 preseason, 2 regular, 3 postseason
 * @param {number} options.week
 * @param {number} [options.limit]
 * @param {number} [options.now]
 * @param {(games: Array) => void} [options.onRevalidated]
 * @param {boolean} [options.backendCache] false keeps the in-session memory
 *   tier but skips storage-backed cache reads and writes.
 */
export async function getSeasonGames({
  onRevalidated,
  backendCache = true,
  ...options
} = {}) {
  const provider = getProvider();
  const now = options.now ?? Date.now();

  if (typeof provider.getSeasonGames !== "function") {
    throw new Error(
      `The ${provider.id} provider cannot browse past seasons. ` +
        "Only the practice game is available in this build."
    );
  }

  const key = seasonScheduleCacheKey(options);

  const live = provider.getSeasonGames(options).then(
    (real) => {
      memory.set(key, {
        games: [...real],
        fetchedAt: real.fetchedAt ?? now,
        provider: provider.id,
        unmapped: Number(real.unmapped) || 0,
      });
      saveSchedule(key, real, provider.id, now, { backendCache });
      return { ok: true, real };
    },
    (error) => ({ ok: false, error })
  );

  const stored = await readSchedule(key, provider.id, { backendCache });

  const fromStored = (entry, extra) =>
    scheduleAnswer([...entry.games], {
      fetchedAt: entry.fetchedAt,
      source: entry.provider,
      fromCache: true,
      unmapped: entry.unmapped,
      ...extra,
    });

  const fromLive = (real, revision) =>
    scheduleAnswer([...real], {
      fetchedAt: real.fetchedAt ?? null,
      source: provider.id,
      revision,
      unmapped: real.unmapped,
    });

  if (stored && now - stored.fetchedAt < archiveCacheTtlMs(stored.games, now)) {
    live.then((result) => {
      if (!onRevalidated) return;

      onRevalidated(
        result.ok
          ? fromLive(result.real, 2)
          : fromStored(stored, { stale: true, error: result.error, revision: 2 })
      );
    });

    return fromStored(stored, { revision: 1 });
  }

  const result = await live;
  if (result.ok) return fromLive(result.real, 2);

  if (stored && !containsLiveGame(stored.games)) {
    return fromStored(stored, { stale: true, error: result.error, revision: 2 });
  }

  throw result.error;
}

const weekKey = (seasonType, week) => `t${seasonType}-w${week}`;

const sectionGameSummary = (games = []) => {
  const final = games.filter((game) => game.status === GAME_STATUS.FINAL).length;
  const live = games.filter((game) => game.status === GAME_STATUS.IN_PROGRESS).length;
  const upcoming = games.filter((game) => game.status === GAME_STATUS.SCHEDULED).length;
  const offSchedule = games.filter((game) => isOffSchedule(game.status)).length;

  if (live > 0) return `${live} live ${live === 1 ? "game" : "games"}`;
  if (upcoming > 0 && final > 0) {
    return `${final} final, ${upcoming} upcoming`;
  }
  if (upcoming > 0) return `${upcoming} upcoming ${upcoming === 1 ? "game" : "games"}`;
  if (offSchedule > 0 && final === 0) {
    return `${offSchedule} not going ahead`;
  }
  if (final > 0) return `${final} final ${final === 1 ? "game" : "games"}`;
  return "No displayable games";
};

/**
 * A full selected season, grouped by ESPN's own phase/week calendar.
 *
 * The fetch still happens one week at a time, through `getSeasonGames`.
 * That is deliberately less clever than a new monolithic cache: old links,
 * existing week cache keys, and stale-while-revalidate behaviour all keep
 * their current meaning while the page graduates from a dropdown-filtered
 * week to collapsible season sections.
 */
export async function getSeasonSchedule({
  year,
  phases = [],
  now = Date.now(),
  limitPerWeek = 64,
  backendCache = true,
} = {}) {
  const selectedYear = Number(year);

  if (!Number.isInteger(selectedYear)) {
    throw new Error("Choose a season before browsing its schedule.");
  }

  const calendar = (phases ?? []).filter(
    (phase) =>
      Number.isInteger(phase?.seasonTypeId) &&
      Array.isArray(phase?.weeks) &&
      phase.weeks.length > 0
  );

  if (calendar.length === 0) {
    throw new Error(`No week structure is available for the ${selectedYear} season.`);
  }

  const loaded = await Promise.all(
    calendar.flatMap((phase) =>
      phase.weeks.map(async (week) => {
        const seasonType = phase.seasonTypeId;
        const weekNumber = week.number;

        try {
          const games = await getSeasonGames({
            year: selectedYear,
            seasonType,
            week: weekNumber,
            limit: limitPerWeek,
            now,
            backendCache,
          });

          return {
            key: weekKey(seasonType, weekNumber),
            seasonType,
            week: weekNumber,
            label: week.label,
            heading:
              weekHeading(calendar, seasonType, weekNumber) ??
              `${phase.label}: ${week.label}`,
            games: [...games],
            fetchedAt: games.fetchedAt ?? null,
            fromCache: Boolean(games.fromCache),
            stale: Boolean(games.stale),
            unmapped: Number(games.unmapped) || 0,
            summary: sectionGameSummary(games),
            error: games.scheduleUnavailable ?? null,
          };
        } catch (error) {
          return {
            key: weekKey(seasonType, weekNumber),
            seasonType,
            week: weekNumber,
            label: week.label,
            heading:
              weekHeading(calendar, seasonType, weekNumber) ??
              `${phase.label}: ${week.label}`,
            games: [],
            fetchedAt: null,
            fromCache: false,
            stale: false,
            unmapped: 0,
            summary: "Unavailable",
            error,
          };
        }
      })
    )
  );

  const sectionsByKey = new Map(loaded.map((section) => [section.key, section]));
  const groups = calendar.map((phase) => ({
    key: `phase-${phase.seasonTypeId}`,
    seasonType: phase.seasonTypeId,
    label: phase.label,
    sections: phase.weeks.map((week) =>
      sectionsByKey.get(weekKey(phase.seasonTypeId, week.number))
    ),
  }));

  const sections = groups.flatMap((group) => group.sections).filter(Boolean);
  const games = sections
    .flatMap((section) => section.games)
    .sort((a, b) => (a.kickoffAt ?? 0) - (b.kickoffAt ?? 0));
  const fetchedAtValues = sections
    .map((section) => section.fetchedAt)
    .filter((stamp) => Number.isFinite(stamp));
  const errors = sections.filter((section) => section.error);

  if (sections.length > 0 && sections.every((section) => section.error)) {
    throw sections[0].error;
  }

  return {
    year: selectedYear,
    groups,
    sections,
    games,
    fetchedAt:
      fetchedAtValues.length > 0 ? Math.min(...fetchedAtValues) : null,
    unmapped: sections.reduce((total, section) => total + section.unmapped, 0),
    partial: errors.length > 0,
    unavailableSections: errors.length,
    provider: getProvider().id,
  };
}

export const getGame = (gameId, options) =>
  providerForGame(gameId).getGame(gameId, options);

export const getLiveGame = (gameId, context) =>
  providerForGame(gameId).getLiveGame(gameId, context);

export const getGameEvents = (gameId, context) =>
  providerForGame(gameId).getGameEvents(gameId, context);

/** State + events in one pass. */
export const getLiveSnapshot = (gameId, context) => {
  const provider = providerForGame(gameId);
  if (provider.getLiveSnapshot) return provider.getLiveSnapshot(gameId, context);

  return Promise.all([
    provider.getLiveGame(gameId, context),
    provider.getGameEvents(gameId, context),
  ]).then(([state, events]) => ({ state, events }));
};

export const getTeam = (teamId) => getProvider().getTeam(teamId);

export const getPlayer = (playerId) => getProvider().getPlayer(playerId);

export async function getSeasonContext({ year } = {}) {
  const provider = getProvider();
  const selectedYear = Number(year);

  if (!Number.isInteger(selectedYear)) {
    return {
      year: null,
      standings: null,
      bracket: null,
      provider: provider.id,
    };
  }

  if (
    typeof provider.getSeasonStandings !== "function" &&
    typeof provider.getSeasonBracket !== "function"
  ) {
    return {
      year: selectedYear,
      standings: null,
      bracket: null,
      provider: provider.id,
    };
  }

  const [standingsResult, bracketResult] = await Promise.allSettled([
    typeof provider.getSeasonStandings === "function"
      ? provider.getSeasonStandings({ year: selectedYear })
      : Promise.resolve(null),
    typeof provider.getSeasonBracket === "function"
      ? provider.getSeasonBracket({ year: selectedYear })
      : Promise.resolve(null),
  ]);

  const standings =
    standingsResult.status === "fulfilled" ? standingsResult.value : null;
  const bracket =
    bracketResult.status === "fulfilled" ? bracketResult.value : null;

  if (!standings && !bracket) {
    const reason =
      standingsResult.status === "rejected"
        ? standingsResult.reason
        : bracketResult.status === "rejected"
          ? bracketResult.reason
          : null;
    if (reason) throw reason;
  }

  return {
    year: selectedYear,
    standings,
    bracket,
    provider: provider.id,
    fetchedAt:
      standings?.fetchedAt && bracket?.fetchedAt
        ? Math.min(standings.fetchedAt, bracket.fetchedAt)
        : (standings?.fetchedAt ?? bracket?.fetchedAt ?? null),
  };
}

export async function getTeamGames(teamId, { year } = {}) {
  const provider = getProvider();
  if (typeof provider.getTeamGames !== "function") return [];
  return provider.getTeamGames(teamId, { year });
}

/** Test-only. */
export function __resetProviderForTests() {
  liveProvider = null;
  memory.clear();
  storedReads.clear();
  writes.clear();
}
